/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
 import * as React from 'react';

 import { makeStyles, styled, Theme } from '@mui/material/styles';
 
 import {FormControlLabel, Checkbox as MuiCheckbox, CheckboxProps as MuiCheckboxProps, Box, Snackbar } from '@mui/material';
 /*
  * ---------------------------------------------------------------------------------
  * Imports - Internal
  * ---------------------------------------------------------------------------------
  */

 
 import { IFieldInputProps, useFormActions } from '@ngt/forms-core';
 import { FormControl, FormControlLabelProps, FormHelperText, FormControlProps } from '@mui/material';
 import { ErrorClassMapping, ErrorStylingType, IAdditionalFormInputProps, FormOptionsContext, IValidationError, useErrorStyling, FormsFeatureContext, BasicAlert } from '@ngt/forms';
 import { Fragment, useContext, useState } from 'react';
import { PostVerifySourceDataField } from '@/api/dtos';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-duotone-svg-icons';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import { LoadingButton } from '@mui/lab';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
 
 /*
  * ---------------------------------------------------------------------------------
  * Interfaces
  * ---------------------------------------------------------------------------------
  */
 
 type SourceDataFormFieldVerifyCheckboxProps = Partial<Omit<MuiCheckboxProps, 'type'>> & IFieldInputProps<boolean, any, IValidationError, boolean> & IAdditionalFormInputProps
 
 export interface ISourceDataFormFieldVerifyCheckboxProps extends SourceDataFormFieldVerifyCheckboxProps {
     FormControlProps?: FormControlProps,
     FormControlLabelProps?: FormControlLabelProps;
     formFieldPropertyPath?: string;
     loading?: boolean;
     setLoading?: (newValue: boolean) => void;
     sourceDataId?: number;
     monitoringVisitId?: number;
 }
 
 /*
  * ---------------------------------------------------------------------------------
  * Styles
  * ---------------------------------------------------------------------------------
  */

const CheckboxCheckIcon = styled(Box)(({ theme }) => ({
    position: 'absolute',
    fontSize: '2em',
    color: theme.palette.success.main
    //color: theme.palette.common.white
}));

const CheckboxIcon = styled(Box)(({ theme }) => ({
    position: 'relative',
    padding: theme.spacing(1),
    minWidth: 0,
    height: 40,
    width: 40,
    border: `1px solid ${theme.palette.primary.main}`
}));
 
 
 /*
  * ---------------------------------------------------------------------------------
  * components
  * ---------------------------------------------------------------------------------
  */
 
 const SourceDataFormFieldVerifyCheckbox: React.FunctionComponent<ISourceDataFormFieldVerifyCheckboxProps> = ({
     FieldInputProps: { state: { propertyPath, value }, actions: { update, blur, focus, touch } },
     label,
     onChange,
     FormControlProps,
     FormControlLabelProps,
     inputProps,
     disabled,
     hasErrors,
     errors,
     errorType,
     formFieldPropertyPath,
     loading,
     setLoading,
     sourceDataId,
     monitoringVisitId,
     ...rest
 }) => {
    const actions = useFormActions();
    const formsFeature = useContext(FormsFeatureContext);
    const [failed, setFailed] = useState(false);
    const [localLoading, setLocalLoading] = useState(false);
    
     const onChangeCombined = React.useCallback(async (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        const id = actions.getFieldValue<number>(`${formFieldPropertyPath}.id`)

        if (id) {
            try {
                setLocalLoading(true);
                setLoading?.(true);
                await formsFeature.serviceStackClient.post(new PostVerifySourceDataField({ sourceDataId: sourceDataId, monitoringVisitId: monitoringVisitId, sourceDataFieldId: id, verified: checked }));
                setLoading?.(false);
                setLocalLoading(false);
            }
            catch 
            {
                setFailed(true);
                setLoading?.(false);
                setLocalLoading(false);
                return;
            }

        }

         if (onChange) {
             onChange(event, checked);
         }
         
        if (checked) {
            actions.setFieldValue(`${formFieldPropertyPath}.reviewed`, true, true, true, true, false);
        }
 
         update(checked, true, true, true);
     }, [onChange, update, actions, formsFeature, setLoading, setFailed, sourceDataId, monitoringVisitId]);
 
     const onFocusCombined = React.useCallback((event: React.FocusEvent<HTMLInputElement>) => {
         if (inputProps?.onFocus) {
             inputProps.onFocus(event);
         }
 
         focus();
     }, [inputProps?.onFocus, focus]);
 
     const onBlurCombined = React.useCallback((event: React.FocusEvent<HTMLInputElement>) => {
         if (inputProps?.onBlur) {
             inputProps.onBlur(event);
         }
 
         blur();
         touch();
     }, [inputProps?.onBlur, blur, touch]);
 
 
     const errorStyling = useErrorStyling(errorType, ErrorStylingType.Input);
 
     return (
        <>
            <FormControl
                key={`${propertyPath}-control`}
                {...FormControlProps}
                error={hasErrors}
                disabled={disabled}
                fullWidth={FormControlProps?.fullWidth === undefined ? true : FormControlProps?.fullWidth}
                sx={{...FormControlProps?.sx, ...errorStyling}}
            >
                <FormControlLabel
                    key={`${propertyPath}-label`}
                    {...FormControlLabelProps}
                    control={
                        <MuiCheckbox
                            key={`${propertyPath}-checkbox`}
                            {...rest as any}
                            inputProps={{
                                ...inputProps,
                                onBlur: onBlurCombined,
                                onFocus: onFocusCombined
                            }}
                            checked={value ?? false}
                            onChange={onChangeCombined}
                            disabled={disabled}
                            color={rest?.color ? rest?.color : 'primary'}
                            checkedIcon={
                                <CheckboxIcon 
                                    component={LoadingButton} 
                                    //@ts-ignore-next-line
                                    variant="contained"
                                    loading={localLoading}
                                    disabled={loading || disabled}
                                >
                                    {/* <CheckboxCheckIcon>
                                        <FontAwesomeIcon icon={faCheck} fixedWidth />
                                    </CheckboxCheckIcon> */}
                                    <FontAwesomeIcon icon={faCheck} fixedWidth />
                                </CheckboxIcon>
                            }
                            icon={
                                <CheckboxIcon 
                                    component={LoadingButton}
                                    //@ts-ignore-next-line
                                    variant="outlined"
                                    loading={localLoading}
                                    disabled={loading  || disabled}
                                >
                                    <FontAwesomeIcon icon={faMagnifyingGlass} fixedWidth />
                                </CheckboxIcon>
                            }
                        />
                    }
                    label={<Fragment key={`${propertyPath}-label`}>{ FormControlLabelProps?.label ?? label }</Fragment>}
                />
                {
                    hasErrors && (
                        <FormHelperText>
                            {errors && errors.length > 0 && errors?.map((error, i) => (<span className={ErrorClassMapping[error.type!]} key={i}>{error.message}</span>))}
                        </FormHelperText>
                    )
                }
            </FormControl>
            <Snackbar
                open={failed}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right'}}
                onClose={() => setFailed(true)}
            >
                <div>
                    <BasicAlert 
                        severity="error"
                        icon={faExclamationTriangle}
                        title="Update Failed"
                        description={
                            `Field was not successfully marked as reviewed, please try again.`
                        }
                    />
                </div>
            </Snackbar>
        </>
     );
 }
 
 /*
  * ---------------------------------------------------------------------------------
  * Default Export
  * ---------------------------------------------------------------------------------
  */
 
 export default SourceDataFormFieldVerifyCheckbox;
 